import { createAsyncThunk } from "@reduxjs/toolkit"
import { type BaseOptionType } from "antd/lib/select"
import i18n from "i18next"

import { errorSave } from "../../../../redux/errors.slice"
import type { RootState } from "../../../../store.ts"
import { type BasePagination } from "../../../../types/api.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import { orderByIds } from "../../../../utils/object/order-by-ids.ts"
import * as services from "../../api/project-checklist.services"
import { type ProjectChecklistItem } from "../../types/project-checklists.types.tsx"
import { projectSetChecklistOrder } from "../project.slice.ts"
import { projectSliceName } from "../project.constants.ts"
import type {
  ProjectChecklistFilterOptions,
  ProjectChecklistFilters
} from "../project.types.ts"

export const fetchChecklistItems = createAsyncThunk<
  { isMore: boolean; data: BasePagination<ProjectChecklistItem> },
  { isMore: boolean; params: ProjectChecklistFilters & { project: string } }
>(
  `${projectSliceName}/fetchChecklistItems`,
  async ({ params, isMore }, { dispatch, rejectWithValue }) => {
    const response = await services.getChecklistItems(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return {
      data: response.data as BasePagination<ProjectChecklistItem>,
      isMore
    }
  }
)

export const fetchChecklistItemsFilterOptions = createAsyncThunk<
  ProjectChecklistFilterOptions,
  string
>(
  `${projectSliceName}/fetchChecklistItemsFilterOptions`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getChecklistItemsFilterOptions(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as ProjectChecklistFilterOptions
  }
)

export const createChecklistItem = createAsyncThunk<
  ProjectChecklistItem,
  Partial<ProjectChecklistItem>
>(
  `${projectSliceName}/createChecklistItem`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createChecklistItem(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, i18n.t("projectTasks.toast.created"))

    return (response?.data?.details?.data ??
      response?.data) as ProjectChecklistItem
  }
)

export const patchChecklistItem = createAsyncThunk<
  ProjectChecklistItem,
  Partial<ProjectChecklistItem> & { id: string }
>(
  `${projectSliceName}/patchChecklistItem`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.patchChecklistItem(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, i18n.t("projectTasks.toast.updated"))
    const res = (response?.data?.details?.data ??
      response?.data) as ProjectChecklistItem

    return { ...res, id: data?.id }
  }
)

export const deleteChecklistItem = createAsyncThunk<string, string>(
  `${projectSliceName}/deleteChecklistItem`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteChecklistItem(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, i18n.t("projectTasks.toast.deleted"))

    return id
  }
)

export const reorderChecklistItems = createAsyncThunk<
  void,
  { id: string; order: number }[],
  { state: RootState }
>(
  `${projectSliceName}/reorderChecklistItems`,
  async (items, { dispatch, getState, rejectWithValue }) => {
    const previous = getState()?.project?.checklistItems?.results || []

    // Move the rows before the request so a dragged task stays where it was
    // dropped; the list is refetched by its own flows afterwards.
    const optimistic = orderByIds(
      previous,
      items.map(({ id }) => id)
    )

    if (optimistic) dispatch(projectSetChecklistOrder(optimistic))

    const response = await services.reorderChecklistItems({ items })

    if (!response.ok) {
      dispatch(projectSetChecklistOrder(previous))
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, i18n.t("projectTasks.toast.reordered"))
  }
)
